import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import React, { useRef } from 'react';
import { Select } from '../../../ui/Select';
import {
  chipsStyles,
  FilterTitle,
  OptionsWrapper,
  RoomsContainer
} from './styled';
import { Chips } from '../../../ui/Chips';
import { CATEGORY_ROOMS } from '../../../../utils/helpers/constants';
import { parseRoomSelection } from '../../../../utils/helpers/parseRoomSelection';
import { FilterFieldProps, SelectRefType } from '../types';
import useScreenSize from '../../../../hooks/useScreenSize';
import { ModalFooter } from '../../../ui/Modal/ModalFooter';
import { actionButtonStyles } from '../styled';
import { getBasedDataStatValueInFilters } from '../../../../utils/helpers/common';

export const RoomsFilter: React.FC<FilterFieldProps> = ({
  submitOnChange = false,
  isPashaLanding
}) => {
  const { t } = useTranslation();
  const { values } = useFormState();
  const { change, submit } = useForm();
  const basedDataStat = getBasedDataStatValueInFilters(isPashaLanding);

  const { isSmall } = useScreenSize();
  const selectRef = useRef<SelectRefType>(null);

  const roomsIds: string[] = Array.isArray(values.room_ids)
    ? values.room_ids
    : [];

  const initialRoomIdsRef = useRef<string[]>(roomsIds);

  const roomsPlaceholder = t('search.form.rooms');
  const { normalRooms, hasFivePlus } = parseRoomSelection(
    roomsIds,
    CATEGORY_ROOMS
  );

  const selectedValue = (() => {
    if (normalRooms.length && hasFivePlus) {
      return `${normalRooms.join(', ')}, ${t('search.form.5+_rooms')}`;
    }

    if (normalRooms.length) {
      return `${normalRooms.join(', ')} ${t('search.form.1-4_rooms')}`;
    }

    if (hasFivePlus) {
      return t('search.form.5+_rooms');
    }

    return '';
  })();

  const handleChangeOption = (itemId: string) => {
    const updatedRooms = roomsIds.includes(itemId)
      ? roomsIds.filter(id => id !== itemId)
      : [...roomsIds, itemId];
    change('room_ids', updatedRooms);
  };

  const onReset = () => {
    change('room_ids', []);
  };

  const handleClose = () => {
    const sortedInitial = [...initialRoomIdsRef.current].sort();
    const sortedCurrent = [...roomsIds].sort();

    const hasChanged =
      sortedInitial.length !== sortedCurrent.length ||
      sortedInitial.some((val, index) => val !== sortedCurrent[index]);

    if (submitOnChange && hasChanged) {
      submit();
      initialRoomIdsRef.current = roomsIds;
    }
  };

  const onSubmit = () => {
    if (selectRef.current) {
      selectRef.current?.closePopover();
    }
    handleClose();
  };

  return (
    <Select
      ref={selectRef}
      placeholder={roomsPlaceholder}
      value={selectedValue}
      cypress={`${basedDataStat}-rooms-filter`}
      stat={`${basedDataStat}-rooms-filter`}
      width={290}
      onClose={handleClose}
      modalTitle={t('search.form.rooms_number')}
      modalCypress="filter-modal"
      showModalHeaderBorder={!isPashaLanding}
    >
      <RoomsContainer>
        {!isSmall && (
          <FilterTitle tag="body_1">
            {t('search.form.rooms_number')}
          </FilterTitle>
        )}
        <OptionsWrapper>
          {CATEGORY_ROOMS.map(room => (
            <Chips
              text={room}
              value={room}
              type="button"
              hasCustomOnChange
              key={`room-${room}`}
              storedValue={roomsIds}
              onClick={() => handleChangeOption(room)}
              styles={chipsStyles}
              cypress={`${basedDataStat}-rooms-${room}`}
              stat={`${basedDataStat}-rooms-${room}`}
            />
          ))}
        </OptionsWrapper>
      </RoomsContainer>
      {isSmall && (
        <ModalFooter
          resetIsDisabled={!roomsIds.length}
          onReset={onReset}
          onConfirm={onSubmit}
          resetText={t('common.reset')}
          confirmText={t('common.apply')}
          confirmCypress="apply-btn"
          styles={actionButtonStyles}
        />
      )}
    </Select>
  );
};
